import { DollarSign, ShoppingCart, Clock, Eye, Sparkles, ArrowRight, FileDown } from "lucide-react"
import { useEffect, useState } from "react"
import SaleDetailsModal from "./SaleDetailsModal"
import { useDispatch, useSelector } from "react-redux"
import { isCatalogStore, isPropertyStore } from "../../utils/storeCategory"
import { getStoreExperience } from "../../utils/storeExperience"
import { allPhonesData, allGeneralSales } from "../../store/actions/userAction"
import * as XLSX from "xlsx"

export default function Sales() {
  const allphonesdata = useSelector((state: any) => state.UserReducer.allphonesdata || [])
  const generalSalesdata = useSelector((state: any) => state.UserReducer.generalSalesdata || [])
  const userdata: any = useSelector((state: any) => state.UserReducer.userdata)

  const { store } = userdata

  const isGeneralStore = isCatalogStore(store)
  const propertyStore = isPropertyStore(store)
  const experience = getStoreExperience(store)

  const salesData = isGeneralStore ? generalSalesdata : allphonesdata
  const sales: any = salesData?.data || []
  const stats = salesData.stats || {
    totalSales: 0,
    totalPaid: 0,
    totalDebt: 0,
  }

  const totalSales = stats.totalSales
  const totalPaid = stats.totalPaid
  const totalDebt = stats.totalDebt
  const pageTitle = propertyStore ? "Transactions immobilieres" : "Ventes"
  const pageSubtitle = propertyStore
    ? "Suivez ventes, locations, encaissements et periodes d'occupation depuis le meme journal."
    : "Historique complet des ventes, paiements reçus et montants restants."

  const [selectedSale, setSelectedSale] = useState<any | null>(null)
  const [exporting, setExporting] = useState(false)

  const pagination = salesData.pagination || {}
  const [page, setPage] = useState(1)

  const dispatch = useDispatch()

  const storeId = store?._id
  const status = "SOLD"
  const limit = 6

  const refreshSales = () => {
    if (!storeId) return
    if (isGeneralStore) {
      dispatch(allGeneralSales(storeId, page, limit, { status }))
      return
    }
    dispatch(allPhonesData(storeId, status, page, limit))
  }

  useEffect(() => {
    refreshSales()
  }, [page, storeId, isGeneralStore])

  const exportExcel = async () => {
    try {
      setExporting(true)

      const rows = sales.map((sale: any) => ({
        Date: sale?.soldAt ? new Date(sale.soldAt).toLocaleString() : "",
        Type: propertyStore ? resolveTransactionType(sale) : isGeneralStore ? "vente produit" : "vente telephone",
        Article: isGeneralStore
          ? sale?.productId?.name || sale?.productName || "Produit"
          : sale?.modelId?.name || "Telephone",
        Reference: isGeneralStore
          ? sale?.productId?.identifier || sale?.identifier || "-"
          : sale?.imei || "-",
        Marque: isGeneralStore ? sale?.productId?.brand || sale?.brand || "-" : sale?.modelId?.brand || "-",
        Client: sale?.clientName || "-",
        Telephone: sale?.clientPhone || "-",
        Quantite: Number(sale?.quantity || 1),
        "Prix unitaire": Number(sale?.unitSellPrice || sale?.sellPrice || 0),
        "Montant total": Number(sale?.sellPrice || 0),
        "Montant paye": Number(sale?.paid || 0),
        Reste: Number(sale?.remaining || 0),
        "Mode paiement": sale?.paymentMethod || "-",
        "Debut location": sale?.rentalStartDate ? new Date(sale.rentalStartDate).toLocaleDateString() : "-",
        "Fin location": sale?.rentalEndDate ? new Date(sale.rentalEndDate).toLocaleDateString() : "-",
      }))

      const worksheet = XLSX.utils.json_to_sheet(rows)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, "Ventes")

      const storeSlug = String(store?.name || "store")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
      const dateStamp = new Date().toISOString().slice(0, 10)

      XLSX.writeFile(workbook, `ventes-${storeSlug || "store"}-${dateStamp}.xlsx`)
    } finally {
      setExporting(false)
    }
  }

  return (
    <>
      <div className="space-y-8">
        <section className={`relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br p-6 sm:p-7 text-white shadow-xl ${propertyStore ? "from-emerald-900 via-teal-700 to-cyan-700" : "from-sky-600 via-blue-700 to-blue-900"}`}>
          <div className="pointer-events-none absolute -top-14 -right-10 h-36 w-36 rounded-full bg-cyan-300/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-12 -left-6 h-32 w-32 rounded-full bg-blue-200/20 blur-3xl" />
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-[11px] uppercase tracking-[0.16em]">
                <Sparkles size={12} /> {propertyStore ? experience.badge : "Sales Control"}
              </p>
              <h1 className="mt-3 text-2xl sm:text-3xl font-bold">{pageTitle}</h1>
              <p className="mt-2 text-sm text-white/85 max-w-2xl">{pageSubtitle}</p>
            </div>

            <div className="rounded-2xl border border-white/25 bg-white/10 px-4 py-3 backdrop-blur-sm">
              <p className="text-xs uppercase tracking-[0.12em] text-white/75">Transactions affichées</p>
              <p className="mt-1 text-xl font-semibold">{sales.length}</p>
            </div>
          </div>

          <div className="relative mt-5 flex flex-wrap gap-2.5">
            <button
              type="button"
              onClick={exportExcel}
              disabled={exporting || sales.length === 0}
              className="inline-flex items-center gap-2 rounded-xl bg-white/90 px-4 py-2 font-medium text-slate-800 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FileDown size={16} />
              {exporting ? "Export..." : "Exporter Excel"}
            </button>
          </div>
        </section>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Kpi
            label={propertyStore ? "Volume transactions" : "Total ventes"}
            value={`${totalSales.toLocaleString()} FCFA`}
            icon={<ShoppingCart size={18} />}
            color={propertyStore ? "from-emerald-600 to-teal-500" : "from-blue-600 to-cyan-600"}
            ring={propertyStore ? "ring-emerald-100" : "ring-blue-100"}
          />
          <Kpi
            label="Total encaissé"
            value={`${totalPaid.toLocaleString()} FCFA`}
            icon={<DollarSign size={18} />}
            color="from-emerald-600 to-green-500"
            ring="ring-emerald-100"
          />
          <Kpi
            label={propertyStore ? "Encours clients" : "Dettes clients"}
            value={`${totalDebt.toLocaleString()} FCFA`}
            icon={<Clock size={18} />}
            color="from-rose-600 to-red-500"
            ring="ring-rose-100"
          />
        </div>

        <div className="hidden md:block overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-6 py-4">
            <p className="text-sm font-semibold text-slate-800">Journal des ventes</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {propertyStore ? "Visualisez les ventes, locations et periodes d'occupation en un coup d'oeil." : "Visualisez les paiements et les restes en un coup d'oeil."}
            </p>
          </div>

          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500">
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">{propertyStore ? "Bien" : isGeneralStore ? "Produit" : "Téléphone"}</th>
                {isGeneralStore && <th className="px-6 py-3">Référence</th>}
                {propertyStore && <th className="px-6 py-3">Type</th>}
                <th className="px-6 py-3">Client</th>
                <th className="px-6 py-3">{propertyStore ? "Montant" : "Prix vente"}</th>
                <th className="px-6 py-3">Payé</th>
                <th className="px-6 py-3">Reste</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {sales.map((s: any) => (
                <tr key={s._id} className="border-t border-slate-100 hover:bg-slate-50/70 transition">
                  <td className="px-6 py-4 text-slate-600">{new Date(s.soldAt).toLocaleString()}</td>

                  <td className="px-6 py-4 font-medium text-slate-800">
                    {isGeneralStore
                      ? s.productId?.name || s.productName || "Produit"
                      : `${s.modelId?.name} (${s.modelId?.brand})`}
                  </td>

                  {isGeneralStore && (
                    <td className="px-6 py-4 text-slate-600">{s.productId?.identifier || s.identifier || "-"}</td>
                  )}

                  {propertyStore && (
                    <td className="px-6 py-4">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${resolveTransactionType(s) === "location" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {resolveTransactionType(s)}
                      </span>
                    </td>
                  )}

                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-slate-800">{s.clientName}</p>
                      <p className="text-xs text-slate-500">{s.clientPhone}</p>
                    </div>
                  </td>

                  <td className="px-6 py-4 text-slate-700 font-medium">{s.sellPrice?.toLocaleString()} FCFA</td>

                  <td className="px-6 py-4">
                    <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                      {s.paid?.toLocaleString()} FCFA
                    </span>
                  </td>

                  <td className="px-6 py-4">
                    {s.remaining > 0 ? (
                      <span className="inline-flex rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
                        {s.remaining.toLocaleString()} FCFA
                      </span>
                    ) : (
                      <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                        Soldé
                      </span>
                    )}
                  </td>

                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => setSelectedSale(s)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 hover:bg-slate-100 transition"
                    >
                      <Eye size={14} /> Voir
                    </button>
                  </td>
                </tr>
              ))}

              {sales.length === 0 && (
                <tr>
                  <td colSpan={propertyStore ? 9 : isGeneralStore ? 8 : 7} className="px-6 py-10 text-center text-slate-500">
                    {propertyStore ? "Aucune transaction immobiliere enregistree" : "Aucune vente enregistrée"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-4 md:hidden">
          {sales &&
            sales.map((s: any) => (
              <div key={s._id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-4 py-3 flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-slate-800">
                      {isGeneralStore ? s.productId?.name || s.productName || "Produit" : s?.modelId?.name}
                    </p>
                    {isGeneralStore && (
                      <p className="text-xs text-slate-500">Réf: {s.productId?.identifier || s.identifier || "-"}</p>
                    )}
                    {propertyStore && (
                      <p className="text-xs text-slate-500">{resolveTransactionType(s)}</p>
                    )}
                    <p className="text-xs text-slate-500 mt-0.5">{new Date(s.soldAt).toLocaleString()}</p>
                  </div>

                  {s.remaining > 0 ? (
                    <span className="text-xs bg-rose-100 text-rose-700 px-2 py-1 rounded-full">Dette</span>
                  ) : (
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">Payé</span>
                  )}
                </div>

                <div className="p-4 space-y-3">
                  <div className="text-sm">
                    <p className="font-medium text-slate-800">{s.clientName}</p>
                    <p className="text-xs text-slate-500">{s.clientPhone}</p>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{propertyStore ? "Transaction" : "Vente"}</span>
                    <span className="font-semibold text-slate-800">{s.sellPrice.toLocaleString()} FCFA</span>
                  </div>

                  {propertyStore && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Periode</span>
                      <span className="text-slate-700">{resolveRentalPeriod(s)}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Payé</span>
                    <span className="text-emerald-700 font-semibold">{s.paid.toLocaleString()} FCFA</span>
                  </div>

                  {s.remaining > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Reste</span>
                      <span className="text-rose-700 font-semibold">{s.remaining.toLocaleString()} FCFA</span>
                    </div>
                  )}

                  <button
                    onClick={() => setSelectedSale(s)}
                    className="w-full inline-flex items-center justify-center gap-1 rounded-xl border border-slate-200 py-2 text-slate-700 hover:bg-slate-50 transition"
                  >
                    Voir détails <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            ))}

          {sales.length === 0 && <div className="text-center text-slate-500 py-10">{propertyStore ? "Aucune transaction immobiliere enregistree" : "Aucune vente enregistrée"}</div>}
        </div>

        {pagination.totalPages > 1 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
            <p className="text-sm text-slate-500">
              Page <strong className="text-slate-700">{pagination.page}</strong> sur{" "}
              <strong className="text-slate-700">{pagination.totalPages}</strong>
            </p>

            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-4 py-2 rounded-xl border border-slate-200 disabled:opacity-50 hover:bg-slate-100 transition"
              >
                Précédent
              </button>

              <button
                disabled={page === pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-4 py-2 rounded-xl bg-blue-600 text-white disabled:opacity-50 hover:bg-blue-700 transition"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      <SaleDetailsModal
        isOpen={Boolean(selectedSale)}
        storeId={store?._id}
        sale={selectedSale}
        onClose={() => setSelectedSale(null)}
        onUpdate={(updatedSale) => {
          setSelectedSale(updatedSale)
          if (isGeneralStore) {
            dispatch(allGeneralSales(storeId, page, limit, { status }))
          } else {
            dispatch(allPhonesData(storeId, status, page, limit))
          }
        }}
        onDeleted={() => {
          setSelectedSale(null)
          refreshSales()
        }}
      />
    </>
  )
}

function resolveTransactionType(sale: any) {
  const type = String(sale?.transactionType || sale?.listingType || "SALE").toUpperCase()

  if (type === "RENT") return "location"
  return "vente"
}

function resolveRentalPeriod(sale: any) {
  const start = sale?.rentalStartDate || sale?.rentStartDate || sale?.startDate
  const end = sale?.rentalEndDate || sale?.rentEndDate || sale?.endDate

  if (!start && !end) return "—"

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })

  return `${start ? formatDate(start) : "—"} -> ${end ? formatDate(end) : "—"}`
}

function Kpi({
  label,
  value,
  icon,
  color,
  ring,
}: {
  label: string
  value: string
  icon: React.ReactNode
  color: string
  ring: string
}) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ring-1 ${ring}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-800 mt-0.5">{value}</p>
        </div>
        <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${color} text-white flex items-center justify-center shadow-lg`}>
          {icon}
        </div>
      </div>
    </div>
  )
}
